<template>
  <Card
    ><template #title=""
      ><div class="p-d-flex p-justify-between">
        แก้ไขสิทธิการใช้งานระบบ
        <div>
          <Button label="บันทึก" icon="pi pi-save" @click="onSaveDocument" />
          <BackButton class="p-ml-2" />
        </div>
      </div>
    </template>
    <template #content="">
      <div class="p-grid">
        <div class="p-col-12 p-lg-4 p-md-6 p-fluid">
          <span class="p-inputgroup">
            <span class="p-inputgroup-addon">
              <i class="pi pi-search"></i>
            </span>
            <InputText v-model="searchString" placeholder="ระบุคำค้น..." />
          </span>
        </div>
        <div class="p-col-12 p-lg-4 p-md-6 p-fluid">
          <span class="p-float-label">
            <Dropdown
              id="searchLevel"
              :options="levels"
              v-model="searchLevel"
            />
            <label for="searchLevel">กรอง Level</label>
          </span>
        </div>
        <div class="p-col-12">
          <DataTable
            :value="filteredUserConfigs"
            paginator
            :rows="10"
            dataKey="employeeId"
          >
            <template #footer="">
              จำนวนทั้งหมด {{ filteredUserConfigs.length }} คน (ทั้งหมด
              {{ userConfigs.length }} คน)
            </template>
            <template #empty="">ไม่พบข้อมูล</template>
            <Column
              header="รหัสพนักงาน"
              field="employeeId"
              sortable
              headerStyle="width: 7rem"
              bodyStyle="width: 7rem"
            ></Column>
            <Column header="ชื่อ" field="name" sortable>
              <template #body="props">
                {{ props.data.employee.prename }}{{ props.data.employee.name }}
                {{ props.data.employee.surname }}
              </template>
            </Column>
            <Column
              header="Level"
              field="employee.level"
              sortable
              headerStyle="width: 5rem"
              bodyStyle="width: 5rem"
            ></Column>
            <Column header="สิทธิ Admin" field="isAdmin" sortable>
              <template #body="props">
                <Checkbox binary v-model="props.data.isAdmin" />
              </template>
            </Column>
            <Column
              header="สิทธิ Onthejob Admin"
              field="isOnTheJobAdmin"
              sortable
            >
              <template #body="props">
                <Checkbox
                  binary
                  v-model="props.data.isOnTheJobAdmin"
                  v-if="props.data.employee.level === '1'"
                />
                <!-- <Checkbox binary :value="true" checked disabled v-else /> </template -->
                <span v-else>
                  <li
                    class="pi pi-check"
                    style="color:green"
                    v-tooltip="
                      `พนักงานตั้งแต่ Level 2 ขึ้นไปได้รับ สิทธิ OnTheJob Admin โดยอัตโนมัติ`
                    "
                  ></li>
                </span>
              </template>
            </Column>
            <Column header="ยอมรับ PDPA แล้ว" field="pdpaAccepted" sortable>
              <template #body="props">
                <i
                  class="pi pi-check"
                  v-if="props.data.isPdpaAccepted"
                  v-tooltip="
                    `ยอมรับล่าสุดเมื่อ ${formatAppDate(
                      props.data.pdpaAcceptOn
                    )}`
                  "
                ></i>
                <i class="pi pi-times" v-else></i>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Card>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { usePlugin } from "@/plugins";
import Fuse from "fuse.js";
import {
  ConfigUser,
  GetConfigUserResponse,
  UpsertConfigUserRequest,
  // GetConfigUserRequest,
} from "@/types";
import { equals, clone } from "ramda";

export default defineComponent({
  name: "ConfigUserScreen",
  setup() {
    const { axios, error, success, setLoading, formatAppDate } = usePlugin();
    const userConfigs = ref<ConfigUser[]>([]);
    const userConfigsTemp = ref<ConfigUser[]>([]);

    const searchString = ref("");
    const searchLevel = ref<string | null>(null);
    const levels = computed(() => {
      return [
        ...new Set(userConfigs.value.map((x) => x.employee?.level ?? "0")),
      ].sort();
    });
    const loadDocument = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetConfigUserResponse>(
          "api/v1/services/configUser",
          {}
          // as GetConfigUserRequest
        );
        userConfigs.value = result.data.configUsers ?? [];
        userConfigsTemp.value = clone(userConfigs.value);
        success("ดึงข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onSaveDocument = async () => {
      setLoading(true);
      try {
        for (const userConfig of userConfigs.value) {
          const userConfigTemp = userConfigsTemp.value.find(
            (x) => x.employeeId === userConfig.employeeId
          );
          if (!equals(userConfigTemp, userConfig)) {
            await axios.post("api/v1/services/configUser/upsert", {
              configUser: userConfig,
            } as UpsertConfigUserRequest);
          }
        }

        success("บันทึกข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const filteredUserConfigs = computed(() => {
      if (!searchString.value && !searchLevel.value) return userConfigs.value;

      let result = userConfigs.value;
      if (searchString.value) {
        const fuse = new Fuse(userConfigs.value, {
          keys: ["employeeId", "employee.name", "employee.surname"],
        });
        const fuzzyResult = fuse.search(searchString.value);
        result = fuzzyResult.map((x) => x.item);
      }
      if (searchLevel.value) {
        result = result.filter((x) => x.employee?.level === searchLevel.value);
      }
      return result;
    });

    const isDirty = computed(
      () => !equals(userConfigs.value, userConfigsTemp.value)
    );

    onMounted(() => loadDocument());

    return {
      filteredUserConfigs,
      userConfigs,
      formatAppDate,
      searchString,
      isDirty,
      onSaveDocument,
      levels,
      searchLevel,
    };
  },
});
</script>

<style></style>
